'use client';

import clsx from 'classnames';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { useOnScreen } from 'ui';

import {
  selectPromotion,
  viewPromotion,
} from '@/modules/google/data-layer/data-layer';
import { Image } from '@/shared/components/image';

import { ImageAndTextSimpleBannerModule } from '../../types';
import styles from './ImageAndTextSimpleBanner.module.scss';

export function ImageAndTextSimpleBanner({
  data,
  imageBreakpoints,
  bannerId,
  name,
  slot,
}: {
  data?: ImageAndTextSimpleBannerModule['data'];
  imageBreakpoints: Record<number, number | number[]>;
  bannerId: number;
  name: string;
  slot?: string | null;
}) {
  const ref = useRef(null);
  const { onScreen, disconnect } = useOnScreen(ref);

  const handleLinkClick = () => {
    selectPromotion(name, `P${bannerId}`, slot);
  };

  useEffect(() => {
    if (!onScreen) {
      return;
    }

    disconnect();
    viewPromotion(name, `P${bannerId}`, slot);
  }, [onScreen]);

  if (!data?.imageDesktop?.url || !data?.imageMobile?.url) return null;

  return (
    <Link
      href={data?.url || '#'}
      prefetch={false}
      className={clsx('Banner', styles.ImageAndTextSimpleBanner)}
      ref={ref}
      onClick={handleLinkClick}
    >
      <Image
        containerClassName={styles.ImageAndTextSimpleBanner__mobileImage}
        src={`${data.imageMobile.url}`}
        breakpoints={imageBreakpoints}
        loader={false}
        width={data.imageMobile.width}
        height={data.imageMobile.height}
        forcePortraitSrcSet={data.imageMobile.height > data.imageMobile.width}
        alt={data.imageAlt}
        title={data.imageTitle}
      />
      <Image
        containerClassName={styles.ImageAndTextSimpleBanner__desktopImage}
        src={`${data.imageDesktop.url}`}
        breakpoints={imageBreakpoints}
        loader={false}
        width={data.imageDesktop.width}
        height={data.imageDesktop.height}
        forcePortraitSrcSet={data.imageDesktop.height > data.imageDesktop.width}
        alt={data.imageAlt}
        title={data.imageTitle}
      />
      {!!data.text1 && (
        <p className={styles.ImageAndTextSimpleBanner__bannerTitle}>
          {data.text1}
        </p>
      )}
      {!!data.text2 && (
        <p className={styles.ImageAndTextSimpleBanner__bannerLink}>
          {data.text2}
        </p>
      )}
    </Link>
  );
}
